/** `emailPurposeTemplates` namespace — en (typed against the French source). */

import type { EmailPurposeTemplatesKeys } from './fr';

const emailPurposeTemplates: EmailPurposeTemplatesKeys = {
  title: 'Purpose templates',
  views: {
    ariaLabel: 'Template view',
    all: 'All templates',
    purposes: 'Purpose templates',
  },
  languages: {
    fr: 'French',
    en: 'English',
    ar: 'Arabic',
  },
  library: {
    subtitle:
      'Library of purpose-based templates (fundraising, press, partnership…) in French, English and Arabic. Built-in templates are read-only: duplicate them to customise.',
    newTemplate: 'New template',
    purposeFilter: 'Purpose',
    allPurposes: 'All purposes',
    languageFilter: 'Language',
    allLanguages: 'All languages',
    includeArchived: 'Show archived templates',
    builtin: 'Built-in',
    custom: 'Custom',
    archived: 'Archived',
    noLanguage: 'Any language',
    subjectLine: 'Subject: {{subject}}',
    count: '{{count}} template(s)',
    empty: 'No template matches these filters.',
    loading: 'Loading the library…',
    loadError: 'Could not load the template library.',
    purposesError: 'Could not load the list of purposes.',
    readOnly:
      'Read-only: you can browse and preview templates, but not create, duplicate, archive or delete them.',
    retry: 'Retry',
  },
  actions: {
    preview: 'Preview',
    duplicate: 'Duplicate',
    edit: 'Edit',
    archive: 'Archive',
    unarchive: 'Unarchive',
    delete: 'Delete',
  },
  toasts: {
    duplicated: 'Editable copy created: “{{name}}”.',
    created: 'Template created.',
    saved: 'Template saved.',
    archived: 'Template archived.',
    unarchived: 'Template unarchived.',
    deleted: 'Template deleted.',
    duplicateFailed: 'Duplication failed.',
    archiveFailed: 'Archiving failed.',
    deleteFailed: 'Deletion failed.',
  },
  confirmDelete: {
    title: 'Delete the template',
    message: 'Delete “{{name}}”? This cannot be undone.',
    confirm: 'Delete',
    cancel: 'Cancel',
  },
  preview: {
    title: 'Preview: {{name}}',
    subject: 'Subject',
    body: 'Body',
    variables: 'Declared variables',
    noVariables: 'This template declares no variable.',
    sampleNote: 'Rendered with sample data — nothing is sent, no credit is used.',
    rendering: 'Rendering…',
    failed: 'The preview failed.',
    close: 'Close',
  },
  sample: {
    first_name: 'Camille',
    last_name: 'Tremblay',
    full_name: 'Camille Tremblay',
    organization: 'Horizon Foundation',
    role: 'Head of Partnerships',
    industry: 'Philanthropy',
    company_size: '50-200',
    last_interaction: 'our chat at the Montréal trade show',
    sender_name: 'Alex Martin',
    sender_signature: 'Alex Martin — Karexia team',
    product_brief: 'a platform that automates communication for organisations',
  },
  variables: {
    first_name: 'First name',
    last_name: 'Last name',
    full_name: 'Full name',
    organization: 'Organisation',
    role: 'Role',
    industry: 'Industry',
    company_size: 'Organisation size',
    last_interaction: 'Last interaction',
    sender_name: 'Sender name',
    sender_signature: 'Sender signature',
    product_brief: 'Product brief',
  },
  form: {
    newTitle: 'New purpose template',
    editTitle: 'Edit the template',
    name: 'Name',
    namePlaceholder: 'e.g. Sponsorship request — SMB',
    purpose: 'Purpose',
    newPurposeOption: '+ New custom purpose…',
    newPurposeLabel: 'New purpose label',
    newPurposePlaceholder: 'e.g. Volunteer recruitment',
    slugHint: 'Identifier: {{slug}}',
    language: 'Language',
    subject: 'Subject',
    subjectPlaceholder: 'e.g. A collaboration with your organisation?',
    body: 'Body (plain text)',
    bodyPlaceholder: 'Hello…',
    bodyHint: 'A blank line separates two paragraphs. The HTML version is generated automatically.',
    insertVariable: 'Insert a variable into the active field',
    create: 'Create',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving…',
    required: 'Name, purpose, subject and body are required.',
    failed: 'Saving failed.',
  },
  compose: {
    tab: 'Compose',
    title: 'Compose an email',
    subtitle: 'One-to-one send from a verified address. Nothing is sent without your explicit confirmation.',
    libraryLink: 'Purpose templates',
    loadingDraft: 'Loading the draft…',
    draftLoadError: 'Could not load the draft.',
    draftFor: 'Draft for {{name}}',
    draftAlreadySent: 'This draft was already sent ({{date}}). It cannot be sent again from here.',
    from: 'From',
    fromPlaceholder: 'Choose a sending address',
    loadingIdentities: 'Loading sending addresses…',
    identitiesError: 'Could not load the sending addresses.',
    noIdentity: 'No verified sending address. Connect and verify a mailbox before sending.',
    noIdentityLink: 'Set up a mailbox',
    to: 'To',
    toPlaceholder: 'recipient@example.com',
    toInvalid: 'Invalid recipient email address.',
    subject: 'Subject',
    body: 'Message (plain text)',
    bodyHint: 'The draft variables are already merged: proofread the text exactly as it will be received.',
    requiredFrom: 'Choose a verified sending address.',
    requiredSubject: 'The subject is required.',
    requiredBody: 'The message is required.',
    saveDraft: 'Save draft',
    savingDraft: 'Saving…',
    draftSaved: 'Draft saved.',
    draftSaveFailed: 'Saving the draft failed.',
    send: 'Send',
    readOnly: 'Read-only: your role does not allow sending emails.',
    confirm: {
      title: 'Confirm sending',
      recipient: 'Recipient',
      subject: 'Subject',
      from: 'From',
      notice:
        'Sending is subject to the compliance check (consent, unsubscribe) — a send without a legal basis will be blocked.',
      confirm: 'Confirm send',
      sending: 'Sending…',
      cancel: 'Cancel',
    },
    blockedTitle: 'Send blocked',
    blockedStatus: 'The email was not sent (status: {{status}}).',
    sendFailed: 'Sending failed.',
    sentTitle: 'Email sent',
    queuedTitle: 'Email queued for sending',
    sentBody: 'Message to {{to}} — id {{id}}.',
    markSentFailed: 'The email went out, but the draft could not be marked as sent.',
    backToContact: 'Back to the contact',
    backToCompany: 'Back to the organisation',
    newMessage: 'New message',
  },
};

export default emailPurposeTemplates;
